import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { DocumentEditorContainerComponent, Toolbar } from '@syncfusion/ej2-react-documenteditor';
import { DocumentEditorProps } from '../types/documentEditor';
import { useStateContext } from '../contexts/ContextProvider';
import './DocumentEditor.css';

DocumentEditorContainerComponent.Inject(Toolbar);

const EmailEditor = forwardRef<any, DocumentEditorProps>(({ 
    height = '100%', 
    initialContent,
    onChange,
    isReadOnly = false
}, ref) => {
    const container = useRef<DocumentEditorContainerComponent>(null);
    const hostUrl = "https://document.syncfusion.com/web-services/docx-editor/api/documenteditor/";
    const { currentMode } = useStateContext();
    
    // Expose the container to parent via ref
    useImperativeHandle(ref, () => ({
        documentEditor: container.current?.documentEditor,
        insertText: (text: string) => {
            if (container.current?.documentEditor) {
                container.current.documentEditor.editor.insertText(text);
                handleContentChange();
            }
        }
    }));
    
    // Handle content changes
    const handleContentChange = () => {
        if (onChange && container.current?.documentEditor) {
            try {
                const sfdt = container.current.documentEditor.serialize();
                onChange(sfdt);
            } catch (error) {
                console.error('Error serializing document:', error);
            }
        }
    };
    
    // Handle theme changes
    useEffect(() => {
        const isDark = currentMode === 'Dark';
        const themeId = 'syncfusion-dark-theme';
        
        // Remove existing dark theme link if present
        const existingLink = document.getElementById(themeId);
        if (existingLink) {
            existingLink.remove();
        }
        
        // Add or remove dark theme
        if (isDark) {
            const link = document.createElement('link');
            link.id = themeId;
            link.rel = 'stylesheet';
            link.href = 'https://cdn.syncfusion.com/ej2/27.1.48/tailwind-dark.css';
            document.head.appendChild(link);
            
            document.body.classList.add('e-dark-mode');
        } else {
            document.body.classList.remove('e-dark-mode');
        }
        
        return () => {
            const link = document.getElementById(themeId);
            if (link) link.remove();
        };
    }, [currentMode]);
    
    // Initial setup
    useEffect(() => {
        if (!container.current) return;
        
        const editor = container.current.documentEditor;
        if (!editor) return;
        
        editor.isReadOnly = isReadOnly;
        editor.documentName = "Email";
        editor.pageOutline = "#E0E0E0";
        editor.acceptTab = true;
        editor.documentEditorSettings.showRuler = false; // Hide ruler for email
        
        container.current.documentChange = (): void => {
            if (container.current) {
                container.current.documentEditor.focusIn();
            }
        };
        
        if (onChange) {
            editor.contentChange = () => {
                handleContentChange();
            };
        }
        
        editor.resize();
        
    }, [isReadOnly]);
    
    // Content loading
    useEffect(() => {
        if (!container.current) return;
        
        const editor = container.current.documentEditor;
        if (!editor) return;
        
        if (initialContent) {
            const timer = setTimeout(() => {
                try {
                    editor.open(initialContent);
                } catch (error) {
                    console.error('Error loading email content:', error);
                }
            }, 50);
            
            return () => clearTimeout(timer);
        }
        
    }, [initialContent]);
    
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
    };
    
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        const text = e.dataTransfer.getData('text/plain');
        if (text && container.current?.documentEditor) {
            container.current.documentEditor.editor.insertText(text);
            handleContentChange();
        }
    };
    
    return (
        <div className="control-pane h-full" onDragOver={handleDragOver} onDrop={handleDrop}>
            <div className="control-section h-full">
                <div id="emaileditor_container_body" className="h-full">
                    <DocumentEditorContainerComponent
                        id="email-container"
                        ref={container}
                        style={{ display: "block" }}
                        height={height}
                        serviceUrl={hostUrl}
                        enableToolbar={false}
                        showPropertiesPane={false}
                        locale="en-US"
                    />
                </div>
            </div>
        </div>
    );
});

EmailEditor.displayName = 'EmailEditor';

export default EmailEditor;
